// src/pages/NotificationsPage.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  MarkEmailRead as ReadIcon,
  MarkEmailUnread as UnreadIcon,
} from "@mui/icons-material";
import API from "../utils/api";
import UnifiedPageWrapper from "../components/Layout/UnifiedPageWrapper";
import SmartTable from "../components/Layout/SmartTable";
import { useGlobalCounts } from "../context/GlobalCountContext";
import { useToast } from "../context/GlobalToastContext";
import { tSuccess, tError, tInfo } from "../utils/toast";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DoneIcon from "@mui/icons-material/Done";
import ConfirmDialog from "../components/ConfirmDialog";
import useAuth from "../hooks/useAuth";
import CrmDialog from "../components/CrmDialog";
import ModalHeader from "../components/ModalHeader";
import ModalFooter from "../components/ModalFooter";



export default function NotificationsPage() {
  const { can } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const { refreshCounts } = useGlobalCounts();
  const { notifySuccess, notifyError } = useToast();

  const TYPE_LABELS = {
  system: "User",
  lead: "Lead",
  territory: "Territory",
  routeplan: "Route Plan",
};


  const fetchNotifications = async () => {
    try {
      const res = await API.get("/notifications/");
      setNotifications(
  res.data.map(n => ({
    ...n,
    mobile_status: n.is_read ? "Read" : "New",   // ⭐ REQUIRED FOR MOBILE
  }))
);

    } catch (err) {
      console.error("Error fetching notifications:", err.response?.data || err.message);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await API.get("/users/");
      setUsers(res.data);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const handleSaveNotification = async () => {
  try {
    if (selectedNotification.id) {
      // Capture the previous read status before updating
      const previousStatus = selectedNotification.is_read;

      await API.put(
        `/notifications/${selectedNotification.id}/`,
        selectedNotification
      );

      await refreshCounts();
      // Trigger info toast based on new status
      if (previousStatus !== selectedNotification.is_read) {
        tInfo(
          selectedNotification.is_read
            ? "Notification marked as read."
            : "Notification marked as unread."
        );
      } else {
        tSuccess("Notification updated successfully!");
      }
    } else {
      await API.post("/notifications/", selectedNotification);
      await refreshCounts();
      tSuccess("Notification created successfully!");
    }

    setOpenEdit(false);
    setSelectedNotification(null);
    await fetchNotifications();
  } catch (err) {
    console.error("Error saving notification:", err);
    tError("Error changing notification status.");
  }
};


  const handleDeleteNotification = (id) => {
    const notification = notifications.find(n => n.id === id);
    setNotificationToDelete(notification);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await API.delete(`/notifications/${notificationToDelete.id}/`);
      await refreshCounts(); 
      notifySuccess("Notification deleted successfully!");
      await fetchNotifications();
      if (selectedNotification?.id === notificationToDelete.id) {
        setOpenEdit(false);
        setSelectedNotification(null);
      }
    } catch (err) {
      console.error("Error deleting notification:", err);
      tError("Failed to delete notification.");
    }
  };

  const handleRowClick = (params) => {
    setSelectedNotification({
      ...params.row,
      user: params.row.user || params.row.user_id || "",
    });
    setOpenEdit(true);
  };

  const handleChange = (field, value) => {
    setSelectedNotification((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    fetchUsers();
    fetchNotifications();
  }, []);

  // 🔹 Updated columns to match core.Notification serializer
  const columns = [


    {
      field: "message",
      headerName: "Message / Title",
      flex: 2,
      renderCell: (params) => params.value || "—",
    },
{
  field: "mobile_status",
  headerName: "Status",
  hide: true,
  flex: 0.8,
  valueGetter: (params) => {
    const row = params.row || {};
    return row.is_read ? "Read" : "New";
  },
},
    {
      field: "user",
      headerName: "User",
      flex: 1,
      valueGetter: (params) => {
        const row = params.row || {};
        return (
          row.user_username ||
          (Array.isArray(users) ? users.find((u) => u.id === row.user)?.username : null) ||
          (row.user ? `User #${row.user}` : "—")
        );
      },
      renderCell: (params) =>
        params.row.user_username ||
        users.find((u) => u.id === params.row.user)?.username ||
        "—",
    },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
      renderCell: (params) => {
  const t = params.row.type;
  return TYPE_LABELS[t] || t || "—";
}

    },
   {
      field: "is_read",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const isRead = Boolean(params.row?.is_read);
        if (isRead) {
          return (
            <Chip
              icon={<DoneIcon sx={{ color: "green !important" }} />}
              label="Read"
              sx={{ bgcolor: "rgba(158, 158, 158, 0.12)", color: "green", fontWeight: 500, borderRadius: "6px" }}
            />
          );
        }
        return (
          <Chip
            icon={<NotificationsActiveIcon sx={{ color: "#bd251a !important" }} />}
            label="New"
            sx={{ bgcolor: "rgba(158, 35, 35, 0.1)", color: "#bd251a", fontWeight: 600, borderRadius: "6px" }}
          />
        );
      },
    },
      

 {
      field: "created_at",
      headerName: "Created At",
      flex: 1.2,
      valueGetter: (params) => {
        const iso = params.row?.created_at;
        if (!iso) return "—";
        const d = new Date(iso);
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
      },
      renderCell: (params) => {
        const iso = params.row?.created_at;
        if (!iso) return "—";
        const d = new Date(iso);
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
      },
    },
];

  return (
    <UnifiedPageWrapper
      title="Notifications"
      subtitle="Compose quick messages,to alert other team members."
      actionButton={
        <Button
          variant="contained"
          onClick={() => {
            setSelectedNotification({
              message: "",
              user: "",
              type: "system",
              is_read: false,
            });
            setOpenEdit(true);
          }}
        >
          + New Notification
        </Button>
      }
    >
       <SmartTable
        rows={notifications}
        columns={columns}
        onRowClick={handleRowClick}
        mobileEssentialFields={["message", "user", "type", "mobile_status", "created_at"]}
        key={JSON.stringify(notifications.map((n) => `${n.id}-${n.is_read}-${n.type}`))}
      />

      <CrmDialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <ModalHeader
  title={selectedNotification?.id ? "Edit Notification" : "New Notification"}
  subtitle="Create a new notification to alert a team member."
/>


        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Message"
              multiline
              rows={3}
              value={selectedNotification?.message || ""}
              onChange={(e) => handleChange("message", e.target.value)}
              fullWidth
            />

            <TextField
  select
  fullWidth
  label="User"
  value={selectedNotification?.user || ""}
  onChange={(e) => handleChange("user", e.target.value)}
  InputLabelProps={{ shrink: true }}
>
  {users.map((u) => (
    <MenuItem key={u.id} value={u.id}>
      {u.username}
    </MenuItem>
  ))}
</TextField>


            <TextField
  select
  fullWidth
  label="Type"
  value={
    selectedNotification?.type && TYPE_LABELS[selectedNotification.type]
      ? selectedNotification.type
      : "system"
  }
  onChange={(e) => handleChange("type", e.target.value)}
  InputLabelProps={{ shrink: true }}
>
  <MenuItem value="system">User</MenuItem>
  <MenuItem value="lead">Lead</MenuItem>
  <MenuItem value="territory">Territory</MenuItem>
  <MenuItem value="routeplan">Route Plan</MenuItem>
</TextField>


            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography>Mark as Read</Typography>
              <Switch
                checked={selectedNotification?.is_read || false}
                onChange={(e) => handleChange("is_read", e.target.checked)}
              />
            </Box>
          </Box>
        </DialogContent>

        <ModalFooter
  showDelete={!!selectedNotification?.id && can.canDelete}
  deleteLabel="Delete"
  onDelete={() => handleDeleteNotification(selectedNotification.id)}
  onCancel={() => setOpenEdit(false)}
  saveLabel="Update"
  onSave={handleSaveNotification}
/>


      </CrmDialog>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          setNotificationToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Notification"
        message="Are you sure you want to delete this notification? This action cannot be undone."
        itemName={notificationToDelete?.message ? `Notification: ${notificationToDelete.message.substring(0, 50)}${notificationToDelete.message.length > 50 ? '...' : ''}` : null}
      />
    </UnifiedPageWrapper>
  );
}
